import React, { useState } from 'react'
import { Textarea } from '../../../lib/ui/textarea'
import { Input } from '../../../lib/ui/input'
import { TrashIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { cn } from '~/lib/utils'
import { useTemplate } from '~/lib/pdf'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import { Button } from '~/lib/ui/button'
import { useTranslation } from '~/hooks/useTranslation'

export const newKey = () => Math.random().toString(36).substr(2, 9)

export const EditorHeading = ({ className, children }) => {
  const headingClasses = cn(
    'mb-4 text-lg font-medium tracking-[0.014px] text-primary',
    className,
  )

  return <div className={headingClasses}>{children}</div>
}

export const ArrayFields = ({
  items,
  keyPrefix,
  onChange,
  onBlur,
  onDeleteClick = () => null,
}) => {
  const handleOnBlur = () => {
    onBlur()
  }
  const { t } = useTranslation('Editor')

  return items.map((item, i) => (
    <div key={`${keyPrefix}-${i}`} className="flex flex-row">
      <Textarea
        rows="2"
        value={item}
        name={`${keyPrefix}-arrayField-${i}`}
        aria-label={`${keyPrefix} item ${i}`}
        onChange={(e) => {
          const updatedItems = [...items]
          updatedItems[i] = e.target.value
          onChange(updatedItems)
        }}
        onBlur={handleOnBlur}
      />
      <button
        onClick={() => onDeleteClick(i)}
        className="max-w-min text-[#b9b9b9]"
        aria-label={t('remove_item')}
      >
        <TrashIcon className="ml-2 w-8" />
      </button>
    </div>
  ))
}

export const ArrayInputs = ({
  items,
  keyPrefix,
  onChange,
  onBlur,
  onKeyPress = () => null,
  onDeleteClick = () => null,
}) => {
  const handleOnBlur = () => {
    onBlur()
  }
  const { t } = useTranslation('Editor')

  return items.map((item, i) => (
    <div key={`${keyPrefix}-${i}`} className="flex flex-row">
      <Input
        value={item}
        name={`${keyPrefix}-arrayInput-${i}`}
        onKeyPress={onKeyPress}
        type="text"
        aria-label={t('prefix_item_num', { prefix: keyPrefix, index: i })}
        onChange={(e) => {
          const updatedItems = [...items]
          updatedItems[i] = e.target.value
          onChange(updatedItems)
        }}
        onBlur={handleOnBlur}
      />
      <button
        onClick={() => onDeleteClick(i)}
        className="max-w-min text-[#b9b9b9]"
        aria-label={t('remove_item_idx', { index: i })}
      >
        <TrashIcon className="ml-2 w-8" />
      </button>
    </div>
  ))
}

export const EditableHeading = ({ title, description, settingName }) => {
  const { t } = useTranslation('Editor')
  const { useTemplateSetting, isSettingSupported } = useTemplate()
  const [settingValue, setSettingValue] = useTemplateSetting(settingName)

  const [open, setOpen] = useState(false)
  const [heading, setHeading] = useState(settingValue)

  const handleSave = () => {
    setSettingValue(heading)
    setOpen(false)
  }

  if (!isSettingSupported(settingName)) {
    return <EditorHeading className="mb-0">{title}</EditorHeading>
  }

  return (
    <>
      <EditorHeading className="mb-0">{settingValue}</EditorHeading>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button size="sm" variant="link" className="ml-2 rounded-none">
            {t('edit')}
            <PencilSquareIcon className="ml-1 w-3.5" />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <DialogDescription>{description}</DialogDescription>
          <div className="flex flex-col gap-4 px-4 pb-4">
            {/* CTS-9213 — input has no associated label; aria-label uses the dialog title for context */}
            <input
              aria-label={title}
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
            />
            <Button onClick={handleSave}>{t('save')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
